import { useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { candidatesApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toaster'

export function NewCandidatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    currentTitle: '',
    currentCompany: '',
    source: '',
    linkedinUrl: '',
  })

  const createCandidate = useMutation({
    mutationFn: (data: typeof form) => candidatesApi.create(data),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] })
      toast({ title: 'Candidate added', variant: 'success' })
      navigate({ to: `/candidates/${data.id}` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to add candidate',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    },
  })

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }))

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const canSubmit = form.firstName && form.lastName && form.email && isValidEmail(form.email)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    createCandidate.mutate(form)
  }

  return (
    <PageLayout fullHeight back="/candidates" title="Add Candidate" maxWidth="2xl">
      <Card className="h-full flex flex-col">
        <CardContent className="flex-1 min-h-0 flex flex-col pt-6">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cand-first">First Name *</Label>
                  <Input id="cand-first" value={form.firstName} onChange={set('firstName')} placeholder="Jane" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-last">Last Name *</Label>
                  <Input id="cand-last" value={form.lastName} onChange={set('lastName')} placeholder="Doe" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-email">Email *</Label>
                  <Input id="cand-email" type="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-phone">Phone</Label>
                  <Input id="cand-phone" value={form.phone} onChange={set('phone')} placeholder="+1 555 123 4567" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-location">Location</Label>
                  <Input id="cand-location" value={form.location} onChange={set('location')} placeholder="San Francisco, CA" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-title">Current Title</Label>
                  <Input id="cand-title" value={form.currentTitle} onChange={set('currentTitle')} placeholder="Software Engineer" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-company">Current Company</Label>
                  <Input id="cand-company" value={form.currentCompany} onChange={set('currentCompany')} placeholder="Acme Corp" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-source">Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                    <SelectTrigger id="cand-source">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="job_board">Job Board</SelectItem>
                      <SelectItem value="agency">Agency</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label htmlFor="cand-linkedin">LinkedIn URL</Label>
                  <Input id="cand-linkedin" value={form.linkedinUrl} onChange={set('linkedinUrl')} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 flex justify-end gap-2 pt-3 border-t mt-3">
              <Button type="button" variant="outline" asChild>
                <Link to="/candidates">Cancel</Link>
              </Button>
              <Button type="submit" disabled={!canSubmit || createCandidate.isPending}>
                {createCandidate.isPending ? 'Adding...' : 'Add Candidate'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageLayout>
  )
}
